import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

/**
 * Completes a reset from the link the email sent.
 *
 * The token lives in the URL's query string, not in anything the user
 * types — this page's entire job is collecting the new password.
 */
export function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit();
  }

  async function submit(): Promise<void> {
    setError(null);
    setIsSubmitting(true);
    try {
      await authApi.confirmPasswordReset(token, newPassword);
      setIsDone(true);
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
      // A rejected password-policy check carries `violations` — i18n keys
      // of its own — rather than a code the generic map below has any
      // wording for; translating them beats "check the highlighted fields"
      // on a page with nothing else to highlight.
      const violations = caught instanceof ApiError ? caught.passwordViolations : [];
      setError(
        violations.length > 0
          ? violations.map((violation) => t(violation)).join(' ')
          : code === 'AUTH_TOKEN_EXPIRED'
            ? // `AUTH_TOKEN_EXPIRED` is shared with session expiry elsewhere, whose
              // copy ("sign in again") makes no sense for a reset link — this page
              // gets its own wording for that one code instead of the generic map.
              t('auth.resetLinkExpired')
            : t(`errors.${code}`, { fallback: t('errors.INTERNAL_ERROR') }),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!token) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <header className="auth-header">
            <LogoMark size={40} />
            <h1 className="auth-title">TABITA</h1>
          </header>
          <Alert tone="danger">{t('errors.AUTH_TOKEN_INVALID')}</Alert>
          <footer className="auth-footer">
            <Link to="/forgot-password" className="back-link">
              {t('auth.forgotPassword')}
            </Link>
          </footer>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
          <p className="auth-subtitle">{t('auth.resetPassword')}</p>
        </header>

        {isDone ? (
          <>
            <Alert tone="success">{t('auth.passwordResetDone')}</Alert>
            <Button
              type="button"
              variant="primary"
              onClick={() => void navigate('/login', { replace: true })}
            >
              {t('auth.backToSignIn')}
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="auth-form">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field
              label={t('auth.newPassword')}
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              autoFocus
              dir="ltr"
            />

            <Button type="submit" variant="primary" loading={isSubmitting}>
              {t('auth.resetPassword')}
            </Button>
          </form>
        )}

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
